import { useMemo, useState, type ReactNode } from "react";
import { AppWindow, Search, SquareKanban } from "lucide-react";
import type { SidebarView } from "@/components/board-sidebar";
import { AgentAvatar } from "@/components/agent-avatar";
import { PALETTE_PAGES } from "@/components/command-palette";
import { viewIcon } from "@/components/windows-menu";
import { Input } from "@/components/ui/input";
import { matchText, normalizeQuery } from "@/lib/palette";
import { useCan } from "@/lib/auth";
import type { AgentWithContext, BoardDetail } from "@/lib/types";

interface Choice {
  key: string;
  view: SidebarView;
  title: string;
  subtitle?: string;
  icon: ReactNode;
  keywords: string[];
}

/**
 * The body of an empty window (Alt+N): what Windows → New window offers — boards,
 * agents (admins), the pages — as one filterable list. Picking fills this window;
 * dropping a board or an agent on it does the same through the workspace's drop.
 */
export function EmptyWindow({
  boards,
  agents,
  canAwait,
  canSeeAgents,
  go,
}: {
  boards: BoardDetail[];
  agents: AgentWithContext[];
  canAwait: boolean;
  canSeeAgents: boolean;
  go: (view: SidebarView) => void;
}) {
  const [query, setQuery] = useState("");
  const isOwner = useCan("owner");
  const groups = useMemo(() => {
    const boardChoices: Choice[] = boards.map((detail) => ({
      key: `board:${detail.board.id}`,
      view: { kind: "board", boardId: detail.board.id },
      title: detail.board.name,
      icon: <SquareKanban />,
      keywords: [],
    }));
    const agentChoices: Choice[] = canSeeAgents
      ? agents.map((agent) => ({
          key: `agent:${agent.id}`,
          view: { kind: "agents", agentId: agent.id },
          title: agent.name,
          subtitle: `@${agent.handle}`,
          icon: <AgentAvatar agent={agent} size="sm" />,
          keywords: [agent.handle],
        }))
      : [];
    const pageChoices: Choice[] = PALETTE_PAGES.filter((page) => (page.admin ? canAwait : true) && (!page.owner || isOwner))
      .filter((page) => canSeeAgents || page.view.kind !== "agents")
      .map((page) => ({ key: `page:${page.view.kind}`, view: page.view, title: page.title, icon: viewIcon(page.view), keywords: page.keywords }));
    const folded = normalizeQuery(query);
    const keep = (choice: Choice) =>
      !folded || matchText(choice.title, folded) !== null || choice.keywords.some((word) => matchText(word, folded) !== null);
    return [
      { label: "Boards", choices: boardChoices.filter(keep) },
      { label: "Agents", choices: agentChoices.filter(keep) },
      { label: "Pages", choices: pageChoices.filter(keep) },
    ].filter((group) => group.choices.length > 0);
  }, [boards, agents, canAwait, canSeeAgents, isOwner, query]);
  const first = groups[0]?.choices[0];

  return (
    <div className="min-h-0 flex-1 overflow-y-auto scrollbar-slim">
      <div className="mx-auto w-full max-w-xl px-4 py-6 win-sm:px-6">
        <div className="mb-4 flex items-center gap-2 text-muted-foreground">
          <AppWindow className="size-4" />
          <p className="text-[13px]">Pick what to show here, or drag a board or agent in.</p>
        </div>
        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && first) {
                event.preventDefault();
                go(first.view);
              }
            }}
            placeholder="Find a board, agent or page…"
            aria-label="Find a board, agent or page"
            className="pl-8"
          />
        </div>
        {groups.length === 0 ? <p className="px-1 text-[13px] text-muted-foreground">Nothing matches “{query.trim()}”.</p> : null}
        {groups.map((group) => (
          <section key={group.label} className="mb-4">
            <h3 className="mb-1 px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{group.label}</h3>
            <ul className="grid gap-0.5 win-md:grid-cols-2">
              {group.choices.map((choice) => (
                <li key={choice.key}>
                  <button
                    type="button"
                    onClick={() => go(choice.view)}
                    className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent focus-visible:bg-accent focus-visible:outline-none [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-muted-foreground"
                  >
                    {choice.icon}
                    <span className="min-w-0 flex-1 truncate">{choice.title}</span>
                    {choice.subtitle ? <span className="truncate text-[11px] text-muted-foreground">{choice.subtitle}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
